/**
 * Bundle Chooser — two-step variant picker for 買X送Y bundle products.
 *
 * Extends the cards picker (<variant-list-cards>) so it inherits the pieces
 * that talk to the rest of the product page: selectVariant() (hidden input,
 * URL, on:variant:change / variant:changed events, price + gallery updates)
 * and addToCart() (cart drawer coordinator, cart:item-added toast).
 *
 * Markup is server-rendered by snippets/bundle-chooser.liquid; this only wires
 * interaction and the mobile sticky bar.
 */
(function () {
  function define() {
    if (customElements.get('bundle-chooser')) return;
    const Base = customElements.get('variant-list-cards');
    if (!Base) return;

    class BundleChooser extends Base {
      init() {
        const jsonEl = this.querySelector('script[data-product-json]');
        if (jsonEl) {
          try {
            const data = JSON.parse(jsonEl.textContent);
            this.productData = data.product;
            this.formattedData = data.formatted;
          } catch (e) {
            console.error('[bundle-chooser] bad product JSON', e);
          }
        }
        this.variantQuantities = {};
        this.selectedId = null;

        this.els = {
          groups: [...this.querySelectorAll('.bc-group')],
          chipSets: [...this.querySelectorAll('.bc-chips')],
          choice: this.querySelector('[data-bc-choice]'),
          price: this.querySelector('[data-bc-price]'),
          orig: this.querySelector('[data-bc-orig]'),
          stock: this.querySelector('[data-bc-stock]'),
          note: this.querySelector('[data-bc-note]'),
          qtyInput: this.querySelector('[data-bc-qty-input]'),
          qtyDec: this.querySelector('[data-bc-qty-dec]'),
          qtyInc: this.querySelector('[data-bc-qty-inc]'),
          add: this.querySelector('[data-bc-add]'),
          sticky: this.querySelector('[data-bc-sticky]'),
          stickyThumb: this.querySelector('[data-bc-sticky-thumb] img'),
          stickyChoice: this.querySelector('[data-bc-sticky-choice]'),
          stickyPrice: this.querySelector('[data-bc-sticky-price]'),
          stickyOrig: this.querySelector('[data-bc-sticky-orig]'),
          stickyShip: this.querySelector('[data-bc-sticky-ship]'),
          stickyAdd: this.querySelector('[data-bc-sticky-add]'),
        };

        this.bindGroups();
        this.bindChips();
        this.bindQty();
        this.bindAdd();
        this.bindSticky();

        // Initial selection: ?variant= wins, else what Liquid marked as selected.
        const urlVariant = new URL(window.location.href).searchParams.get('variant');
        const marked = this.querySelector('.bc-chip.is-selected');
        const initial =
          (urlVariant && this.querySelector(`.bc-chip[data-variant-id="${urlVariant}"]`)) ||
          marked ||
          this.querySelector('.bc-chip:not(.is-unavailable)');
        if (initial) this.selectChip(initial, false);
      }

      /* ---------------------------------------------------------- step 1 */
      bindGroups() {
        this.els.groups.forEach((btn) => {
          btn.addEventListener('click', () => this.selectGroup(btn.dataset.groupKey));
        });
      }

      selectGroup(key, updateUrl = true) {
        this.els.groups.forEach((g) => {
          const on = g.dataset.groupKey === key;
          g.classList.toggle('is-active', on);
          g.setAttribute('aria-checked', String(on));
        });
        let activeSet = null;
        this.els.chipSets.forEach((set) => {
          const on = set.dataset.groupKey === key;
          set.classList.toggle('is-active', on);
          if (on) activeSet = set;
        });
        if (!activeSet) return;

        // Keep the same 砂種 when hopping between bundles if it exists there.
        const current = this.querySelector('.bc-chip.is-selected');
        const wantType = current ? current.dataset.buyType : null;
        const same = wantType
          ? activeSet.querySelector(`.bc-chip[data-buy-type="${CSS.escape(wantType)}"]:not(.is-unavailable)`)
          : null;
        const next = same || activeSet.querySelector('.bc-chip:not(.is-unavailable)') || activeSet.querySelector('.bc-chip');
        if (next) this.selectChip(next, updateUrl);
      }

      /* ---------------------------------------------------------- step 2 */
      bindChips() {
        this.querySelectorAll('.bc-chip').forEach((chip) => {
          chip.addEventListener('click', () => {
            if (chip.disabled) return;
            this.selectChip(chip, true);
          });
        });
      }

      selectChip(chip, updateUrl = true) {
        const variantId = chip.dataset.variantId;
        this.querySelectorAll('.bc-chip').forEach((c) => {
          const on = c === chip;
          c.classList.toggle('is-selected', on);
          c.setAttribute('aria-checked', String(on));
        });
        // Make sure the chip's group is the active one (initial load / URL variant).
        const set = chip.closest('.bc-chips');
        if (set && !set.classList.contains('is-active')) {
          const key = set.dataset.groupKey;
          this.els.groups.forEach((g) => {
            const on = g.dataset.groupKey === key;
            g.classList.toggle('is-active', on);
            g.setAttribute('aria-checked', String(on));
          });
          this.els.chipSets.forEach((s) => s.classList.toggle('is-active', s === set));
        }

        this.selectedId = variantId;
        if (!this.variantQuantities[variantId]) {
          this.variantQuantities[variantId] = this.currentQty();
        }
        this.els.add.dataset.variantId = variantId;
        if (this.els.stickyAdd) this.els.stickyAdd.dataset.variantId = variantId;

        // Base handles hidden input, URL, events, main price, gallery image.
        this.selectVariant(variantId, updateUrl);
        this.renderSummary(variantId);
      }

      renderSummary(variantId) {
        const variant = this.productData?.variants?.find((v) => String(v.id) === String(variantId));
        const f = this.formattedData?.[variantId] || {};
        if (!variant) return;

        const choice = f.choice || variant.title;
        const price = f.price || '';
        const soldOut = !variant.available && f.policy !== 'continue';
        const low = f.tracked && f.inventory > 0 && f.inventory <= Number(this.dataset.lowStockThreshold || 10);

        if (this.els.choice) this.els.choice.textContent = choice;
        if (this.els.price) this.els.price.textContent = price;
        if (this.els.orig) this.els.orig.textContent = f.original ? `原價 ${f.original}` : '';
        if (this.els.stock) {
          this.els.stock.className = 'bc-stock ' + (soldOut ? 'bc-stock--out' : low ? 'bc-stock--low' : 'bc-stock--in');
          this.els.stock.textContent = soldOut ? '已售完' : low ? `剩 ${f.inventory} 件` : '有庫存';
        }
        if (this.els.note) {
          const ship = f.ship || '';
          // 4-pack bundles cannot ship to a convenience store (carrier rule in the
          // shipping app); say so here — 68 shoppers a month removed a 4+2 from
          // the cart, most of them to swap to a 2+1.
          this.els.note.textContent = /宅配/.test(ship)
            ? '此組合宅配免運，直接送到家；想超商取貨請選「買2送1」。'
            : /7-11|超商/.test(ship)
              ? '此組合 7-11 超商取貨免運（4 包以上限宅配）。'
              : '';
        }

        [this.els.add, this.els.stickyAdd].forEach((btn) => {
          if (!btn) return;
          btn.disabled = soldOut;
          const label = btn.querySelector('.bc-add__label') || btn;
          if (!btn.classList.contains('is-added')) {
            label.textContent = soldOut ? '已售完' : '加入購物車';
          }
        });

        // Sticky mirror
        if (this.els.stickyChoice) this.els.stickyChoice.textContent = choice;
        if (this.els.stickyPrice) this.els.stickyPrice.textContent = price;
        if (this.els.stickyOrig) this.els.stickyOrig.textContent = f.original || '';
        if (this.els.stickyShip) {
          const ship = f.ship || '';
          this.els.stickyShip.textContent = /宅配/.test(ship) ? '宅配免運' : /7-11|超商/.test(ship) ? '超商免運' : '';
        }
        if (this.els.stickyThumb) {
          const src = variant.featured_image?.src;
          if (src) this.els.stickyThumb.src = `${src.replace(/\?.*$/, '')}?width=96`;
        }
      }

      /* ---------------------------------------------------------- quantity */
      currentQty() {
        const n = parseInt(this.els.qtyInput?.value, 10);
        return Number.isFinite(n) && n > 0 ? Math.min(n, 99) : 1;
      }

      setQty(n) {
        const qty = Math.max(1, Math.min(99, n || 1));
        if (this.els.qtyInput) this.els.qtyInput.value = qty;
        if (this.els.qtyDec) this.els.qtyDec.disabled = qty <= 1;
        if (this.selectedId) this.variantQuantities[this.selectedId] = qty;
      }

      bindQty() {
        this.els.qtyDec?.addEventListener('click', () => this.setQty(this.currentQty() - 1));
        this.els.qtyInc?.addEventListener('click', () => this.setQty(this.currentQty() + 1));
        this.els.qtyInput?.addEventListener('input', () => this.setQty(this.currentQty()));
        this.els.qtyInput?.addEventListener('focus', () => this.els.qtyInput.select());
      }

      /* ---------------------------------------------------------- add */
      bindAdd() {
        [this.els.add, this.els.stickyAdd].forEach((btn) => {
          if (!btn) return;
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            if (btn.disabled) return;
            // Base.addToCart reads variantQuantities[variantId]; keep it fresh.
            this.variantQuantities[btn.dataset.variantId] = this.currentQty();
            this.addToCart(btn);
          });
        });
      }

      // Base calls this after a successful add expecting a .vlc-variant card;
      // here we flash the pressed button instead.
      showAddedState(btn) {
        const targets = [this.els.add, this.els.stickyAdd].filter(Boolean);
        targets.forEach((b) => {
          b.classList.add('is-added');
          const label = b.querySelector('.bc-add__label') || b;
          label.dataset.prev = label.textContent;
          label.textContent = '已加入 ✓';
        });
        clearTimeout(this._addedTimer);
        this._addedTimer = setTimeout(() => {
          targets.forEach((b) => {
            b.classList.remove('is-added');
            const label = b.querySelector('.bc-add__label') || b;
            label.textContent = label.dataset.prev || '加入購物車';
          });
        }, 1800);
      }

      /* ---------------------------------------------------------- sticky */
      bindSticky() {
        if (!this.els.sticky || !this.els.add) return;
        const mq = window.matchMedia('(max-width: 768px)');

        // "Past" = the add button's bottom edge has scrolled above the viewport.
        // Measured directly on each scroll rather than via IntersectionObserver:
        // IO callbacks wait for a paint, and a backgrounded/hidden tab can go
        // several seconds without one, leaving the bar stuck in the wrong state.
        const pastButton = () => this.els.add.getBoundingClientRect().bottom < 0;

        const apply = () => {
          const show = mq.matches && pastButton() && !this.drawerOpen();
          this.els.sticky.classList.toggle('is-visible', show);
          this.els.sticky.setAttribute('aria-hidden', String(!show));
          document.body.classList.toggle('bc-sticky-on', show);
        };

        let ticking = false;
        const onScroll = () => {
          if (ticking) return;
          ticking = true;
          const run = () => { ticking = false; apply(); };
          if ('requestAnimationFrame' in window) requestAnimationFrame(run); else setTimeout(run, 16);
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        mq.addEventListener ? mq.addEventListener('change', apply) : mq.addListener(apply);
        document.addEventListener('on:cart-drawer:open', apply);
        document.addEventListener('on:cart-drawer:close', apply);
        document.addEventListener('dispatch:cart-drawer:open', () => setTimeout(apply, 50));
        document.addEventListener('dispatch:cart-drawer:close', () => setTimeout(apply, 350));
        this._applySticky = apply;
        apply();
      }

      drawerOpen() {
        const drawer = document.querySelector('cart-drawer');
        return !!(drawer && (drawer.hasAttribute('open') || drawer.classList.contains('is-open') || drawer.getAttribute('aria-hidden') === 'false'));
      }

      disconnectedCallback() {
        document.body.classList.remove('bc-sticky-on');
      }
    }

    customElements.define('bundle-chooser', BundleChooser);
  }

  // variant-list-cards.js is loaded with defer as well; it may land after us.
  if (customElements.get('variant-list-cards')) {
    define();
  } else {
    customElements.whenDefined('variant-list-cards').then(define);
  }
})();
